import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Banknote, Scale } from 'lucide-react';
import { subFinancialsApi } from '../api';
import { centsToDollarString } from '../constants';
import { balanceLabel } from '../subConstants';
import { CommitmentsView } from '../components/CommitmentsView';
import { SubPaymentsList } from '../components/SubPaymentsList';
import { SubPaymentFormDialog } from '../components/SubPaymentFormDialog';

const TABS = [
  { key: 'commitments', label: 'Commitments' },
  { key: 'payments', label: 'Payments' },
  { key: 'balances', label: 'Balances' },
];

const balanceColor = (c) => (c > 0 ? 'text-[#D4AF37]' : c < 0 ? 'text-red-400' : 'text-gray-200');

export default function OpsSubcontractorCosts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = TABS.some((t) => t.key === tabParam) ? tabParam : 'commitments';
  const [refreshKey, setRefreshKey] = useState(0);
  const [payOpen, setPayOpen] = useState(false);
  const [balances, setBalances] = useState([]);
  const [groupBy, setGroupBy] = useState('subcontractor');

  const bump = () => setRefreshKey((k) => k + 1);

  const loadBalances = useCallback(() => {
    subFinancialsApi.balances({ group_by: groupBy }).then((r) => setBalances(r.data)).catch(() => {});
  }, [groupBy]);

  useEffect(() => {
    if (tab === 'balances') loadBalances();
  }, [tab, loadBalances, refreshKey]);

  return (
    <div data-testid="ops-subcontractor-costs">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">Subcontractor Costs</h2>
          <p className="text-gray-400 mt-1">Track project commitments, payments, and remaining subcontractor balances.</p>
        </div>
        {tab === 'payments' && (
          <Button onClick={() => setPayOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="subcost-record-payment-btn">
            <Banknote size={16} /> Record Payment
          </Button>
        )}
      </div>

      <div className="flex gap-1 border-b border-gray-800 mb-6 pb-px overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setSearchParams(t.key === 'commitments' ? {} : { tab: t.key })}
            className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key ? 'border-[#D4AF37] text-[#D4AF37] font-medium' : 'border-transparent text-gray-400 hover:text-white'
            }`}
            data-testid={`subcost-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'commitments' && <CommitmentsView refreshKey={refreshKey} onChanged={bump} />}

      {tab === 'payments' && <SubPaymentsList showFilters refreshKey={refreshKey} onChanged={bump} />}

      {tab === 'balances' && (
        <div data-testid="subcost-balances">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm text-gray-400">Group by:</span>
            <button
              onClick={() => setGroupBy('subcontractor')}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${groupBy === 'subcontractor' ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-medium' : 'border-gray-700 text-gray-300'}`}
              data-testid="subcost-group-sub"
            >
              Subcontractor
            </button>
            <button
              onClick={() => setGroupBy('project_subcontractor')}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${groupBy === 'project_subcontractor' ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-medium' : 'border-gray-700 text-gray-300'}`}
              data-testid="subcost-group-proj"
            >
              Project &amp; Subcontractor
            </button>
          </div>

          {balances.length === 0 ? (
            <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="subcost-balances-empty">
              <Scale size={32} className="mx-auto mb-3 text-gray-700" />
              <p className="text-gray-500">No counted commitments or recorded payments yet.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {balances.map((b) => (
                <button
                  key={`${b.subcontractor_id}-${b.project_id || 'all'}`}
                  onClick={() => navigate(`/ops/people/subcontractors/${b.subcontractor_id}`)}
                  className="w-full flex flex-wrap items-center justify-between gap-x-4 gap-y-1 bg-[#111111] hover:bg-[#1A1A1A] border border-gray-800 rounded-lg px-4 py-3 text-left transition-colors"
                  data-testid={`subcost-balance-${b.subcontractor_id}${b.project_id ? `-${b.project_id}` : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-medium">{b.subcontractor?.display_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {b.project ? `${b.project.name} · ` : ''}
                      {b.commitment_count} counted commitment{b.commitment_count === 1 ? '' : 's'} · {balanceLabel(b.known_balance_cents)}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm shrink-0">
                    <span className="text-gray-400">Committed <span className="text-gray-200">{centsToDollarString(b.counted_commitment_cents)}</span></span>
                    <span className="text-gray-400">Paid <span className="text-gray-200">{centsToDollarString(b.recorded_payments_cents)}</span></span>
                    <span className={`font-semibold ${balanceColor(b.known_balance_cents)}`}>{centsToDollarString(b.known_balance_cents)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <SubPaymentFormDialog open={payOpen} onOpenChange={setPayOpen} onSaved={bump} />
    </div>
  );
}
